import { useTranslation } from 'react-i18next';

import CaseIcon from '@crm/shared/assets/icons/case.svg?react';
import HelmetIcon from '@crm/shared/assets/icons/helmet.svg?react';
import { cn } from '@crm/shared/lib/styles/utils';
import { Button, SvgIcon, Tooltip, TooltipContent, TooltipTrigger } from '@crm/shared/ui';

import { useModuleSwitch } from '../model/useModuleSwitch';

import type { TCrmModuleSwitcherProps } from '../model/moduleSwitch';

export const CrmModuleSwitcher = ({ source }: TCrmModuleSwitcherProps) => {
    const { t } = useTranslation();
    const { isSwitching, switchModule } = useModuleSwitch(source);
    const constructionLabel = t('crm.smetterModuleSwitcher.construction');
    const salesLabel = t('crm.smetterModuleSwitcher.sales');

    return (
        <div className="relative flex items-center rounded-lg bg-stone-50">
            <span
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute inset-y-0 left-1/2 w-1/2 rounded-lg bg-white shadow-[0px_0px_8px_0px_rgba(0,0,0,0.05)] transition-transform duration-200 ease-out motion-reduce:transition-none',
                    isSwitching && '-translate-x-full'
                )}
            />
            <Tooltip>
                <TooltipTrigger
                    render={
                        <Button
                            type="button"
                            variant="ghost"
                            aria-label={constructionLabel}
                            aria-disabled={isSwitching}
                            className={cn(
                                'relative z-10 h-auto flex-1 border-none px-2.75 py-2',
                                isSwitching
                                    ? 'cursor-default hover:bg-transparent hover:opacity-100'
                                    : 'hover:bg-white hover:opacity-50 hover:shadow-[0px_0px_8px_0px_rgba(0,0,0,0.05)]'
                            )}
                            onClick={switchModule}
                        >
                            <SvgIcon
                                icon={HelmetIcon}
                                size={16}
                                className={cn(
                                    'transition-colors duration-200 [&_path]:fill-current',
                                    isSwitching
                                        ? 'text-smtr-main-accent-100'
                                        : 'text-smtr-main-tertiary-100'
                                )}
                            />
                        </Button>
                    }
                />
                <TooltipContent side="bottom">{constructionLabel}</TooltipContent>
            </Tooltip>

            <Tooltip>
                <TooltipTrigger
                    render={
                        <Button
                            type="button"
                            variant="ghost"
                            aria-label={salesLabel}
                            aria-disabled="true"
                            className="relative z-10 h-auto flex-1 cursor-default border-none px-2.75 py-2 hover:bg-transparent active:not-aria-[haspopup]:translate-y-0"
                        >
                            <SvgIcon
                                icon={CaseIcon}
                                size={16}
                                className={cn(
                                    'transition-colors duration-200 [&_path]:fill-current',
                                    isSwitching
                                        ? 'text-smtr-main-tertiary-100'
                                        : 'text-smtr-main-accent-100'
                                )}
                            />
                        </Button>
                    }
                />
                <TooltipContent side="bottom">{salesLabel}</TooltipContent>
            </Tooltip>
        </div>
    );
};
